const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = async (endpoint, method = 'GET', body = null) => {
  const options = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };
  if (body) options.body = JSON.stringify(body);
  const res = await fetch(`${BASE_URL}${endpoint}`, options);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) return null;
  return res.json();
};

// ── Players ──────────────────────────────────────────────────────────────────
export const getPlayers    = ()         => api('/api/players');
export const createPlayer  = (data)     => api('/api/players', 'POST', data);
export const updatePlayer  = (id, data) => api(`/api/players/${id}`, 'PUT', data);

// ── Wellness ─────────────────────────────────────────────────────────────────
export const getWellness   = ()     => api('/api/wellness');
export const saveWellness  = (data) => api('/api/wellness', 'POST', data);

// ── RPE ──────────────────────────────────────────────────────────────────────
export const getRPE        = ()     => api('/api/rpe');
export const saveRPE       = (data) => api('/api/rpe', 'POST', data);
export const getRPEAnalysis = (dateFrom, dateTo) =>
  api(`/api/rpe/analysis${dateFrom && dateTo ? `?date_from=${dateFrom}&date_to=${dateTo}` : ''}`);

// ── GPS ──────────────────────────────────────────────────────────────────────
export const getGPS           = ()                       => api('/api/gps');
export const saveGPS          = (data)                   => api('/api/gps', 'POST', data);
export const deleteGPS        = (id)                     => api(`/api/gps/${id}`, 'DELETE');
export const patchGPSMaxSpeed = (sessionId, playerId, v) => api(`/api/gps/${sessionId}`, 'PATCH', { player_id: playerId, max_speed: v });
export const updateSessionNotes = (sessionId, notes) => api(`/api/gps/${sessionId}/notes`, 'PUT', { notes });
export const getGpsSessionPlayers = (sessionId) => api(`/api/gps/sessions/${sessionId}/players`);
export const previewGpsEstimate = (sessionId, playerId, missedMins) =>
  api(`/api/gps/sessions/${sessionId}/estimate`, 'POST', { player_id: playerId, missed_mins: missedMins });
export const commitGpsEstimate = (sessionId, playerId, missedMins, userId, requesterRole, metrics) =>
  api(`/api/gps/sessions/${sessionId}/commit-estimate`, 'POST', { player_id: playerId, missed_mins: missedMins, user_id: userId, requesterRole, metrics });
export const commitGpsManualValues = (sessionId, playerId, userId, requesterRole, values) =>
  api(`/api/gps/sessions/${sessionId}/commit-manual`, 'POST', { player_id: playerId, user_id: userId, requesterRole, values });
export const revertGpsEstimate = (sessionId, playerId, userId, requesterRole) =>
  api(`/api/gps/sessions/${sessionId}/revert-estimate`, 'POST', { player_id: playerId, user_id: userId, requesterRole });

// ── Scenarios ─────────────────────────────────────────────────────────────────
export const getScenarios    = ()     => api('/api/scenarios');
export const createScenario  = (data) => api('/api/scenarios', 'POST', data);
export const updateScenario  = (id, data) => api(`/api/scenarios/${id}`, 'PUT', data);
export const deleteScenario  = (id)   => api(`/api/scenarios/${id}`, 'DELETE');

// ── Player position history (date-scoped) ──────────────────────────────────────
export const getPlayerPositions    = ()     => api('/api/player-positions');
export const createPlayerPosition  = (data) => api('/api/player-positions', 'POST', data);
export const deletePlayerPosition  = (id)   => api(`/api/player-positions/${id}`, 'DELETE');

// ── Trainer ratings (Tactical/Identity) ─────────────────────────────────────────
export const getKpiDefinitions      = (pillar) => api(`/api/kpi-definitions${pillar ? `?pillar=${pillar}` : ''}`);
export const getTrainerRatings      = (params) => api(`/api/trainer-ratings?${new URLSearchParams(params)}`);
export const saveMatchRatings       = (data)   => api('/api/trainer-ratings/match', 'POST', data);
export const saveTrainingRatings    = (data)   => api('/api/trainer-ratings/training', 'POST', data);
export const deleteTrainerRatings   = (params) => api(`/api/trainer-ratings?${new URLSearchParams(params)}`, 'DELETE');

// ── 360-view pillar scores (Stage 3 rollup) ─────────────────────────────────────
export const getPlayerPillarScores = (playerId, period) => api(`/api/players/${playerId}/pillar-scores?period=${period}`);
export const getPlayerPillarTrend  = (playerId, period) => api(`/api/players/${playerId}/pillar-scores-trend?period=${period}`);
export const getSquadPillarScores  = (period) => api(`/api/players/pillar-scores?period=${period}`);
export const getPhysicalBreakdown  = (playerId, period) => api(`/api/players/${playerId}/physical-breakdown?period=${period}`);
export const getPillarKpiBreakdown = (pillar, period) => api(`/api/players/pillar-kpi-breakdown?pillar=${pillar}&period=${period}`);
// matchIds (optional, array of match_id): when given, overrides `period` entirely — shows one
// specific game's own numbers, or the pooled average across several selected games.
export const getTechnicalKpiBreakdown = (period, matchIds) => {
  const q = (matchIds && matchIds.length) ? `match_ids=${matchIds.join(',')}` : `period=${period}`;
  return api(`/api/players/technical-kpi-breakdown?${q}`);
};
export const getPillarWeights      = ()       => api('/api/pillar-weights');
export const getFormations         = ()       => api('/api/formations');
// period ('season' | 'preseason', optional): overrides `limit` entirely — every match in that
// window instead of just the last N.
export const getPlayerTechnicalTrend = (playerId, { limit = 8, period } = {}) =>
  api(`/api/players/${playerId}/technical-trend?${period ? `period=${period}` : `limit=${limit}`}`);

// ── Individual Progress report (2026-08-05) — historical per-scenario-week scores, distinct
// from the week/28day/season periods above (always anchored to real today). "Weeks" are team
// scenario instances, listed by getIndividualProgressWeeks and referenced by scenarioId.
export const getIndividualProgressWeeks = () => api('/api/individual-progress/weeks');
export const getIndividualProgress = (playerIds, scenarioIds) =>
  api(`/api/individual-progress?player_ids=${playerIds.join(',')}&scenario_ids=${scenarioIds.join(',')}`);

// ── Stage 7c: lineup save/load/default ──────────────────────────────────────────
export const getLineups          = ()               => api('/api/lineups');
export const getDefaultLineup    = ()               => api('/api/lineups/default');
export const getLineup           = (id)              => api(`/api/lineups/${id}`);
export const createLineup        = (data)            => api('/api/lineups', 'POST', data);
export const updateLineup        = (id, data)        => api(`/api/lineups/${id}`, 'PUT', data);
export const setDefaultLineup    = (id)              => api(`/api/lineups/${id}/set-default`, 'POST');

// ── Official Line-up (2026-08-07) ────────────────────────────────────────────
export const getMatchesForLineup   = ()                    => api('/api/matches/for-lineup');
export const getOfficialLineup     = (matchDate, opponentName) =>
  api(`/api/lineups/official?${new URLSearchParams({ matchDate, opponentName })}`);
export const saveOfficialLineup    = (data)                 => api('/api/lineups/official', 'POST', data);
export const getOfficialLineupStats = ()                    => api('/api/lineups/official/stats');

// ── Auth ──────────────────────────────────────────────────────────────────────
export const loginUser = (username, password) =>
  api('/api/auth/login', 'POST', { username, password });

// ── Users ─────────────────────────────────────────────────────────────────────
export const getUsers   = ()         => api('/api/users');
export const createUser = (data)     => api('/api/users', 'POST', data);
export const updateUser = (id, data) => api(`/api/users/${id}`, 'PUT', data);
export const deleteUser        = (id)                           => api(`/api/users/${id}`, 'DELETE');
export const changePassword    = (userId, currentPassword, newPassword) =>
  api('/api/auth/change-password', 'PUT', { userId, currentPassword, newPassword });
export const getUserPassword   = (id)                           => api(`/api/users/${id}/password`);

// ── Usage tracking (2026-08-23) — admin-only overview of who's using the app, when, and how much.
// trackUsage swallows its own errors: it's fire-and-forget instrumentation on every navigation,
// and a failed log call should never surface as a broken page to the user.
export const trackUsage = (userId, username, name, page) =>
  api('/api/usage/track', 'POST', { userId, username, name, page }).catch(() => {});
export const getUsageSummary = (requesterId) => api(`/api/usage/summary?requesterId=${requesterId}`);

// ── Opponents ─────────────────────────────────────────────────────────────────
export const getOpponents    = ()         => api('/api/opponents');
export const createOpponent  = (data)     => api('/api/opponents', 'POST', data);
export const updateOpponent  = (id, data) => api(`/api/opponents/${id}`, 'PUT', data);
export const seedOpponents   = ()         => api('/api/opponents/seed', 'POST');

// ── Goalkeeper sessions ───────────────────────────────────────────────────────
export const getGkSessions    = ()     => api('/api/gk-sessions');
export const uploadGkSessions = (rows) => api('/api/gk-sessions/upload', 'POST', rows);
export const deleteGkSession  = (sessionDate, sessionTitle) => {
  const q = new URLSearchParams({ session_date: sessionDate, session_title: sessionTitle });
  return api(`/api/gk-sessions/session?${q}`, 'DELETE');
};

// ── Technical stats (SciSports CSV import) ──────────────────────────────────────
// Bypasses the shared api() helper — its generic "API error: 400" would swallow this
// endpoint's specific match-resolution error message, but a clear error (not a silent skip)
// is the whole point of that check (see technicalImport.js). Doesn't touch api() itself,
// which every other caller in the app relies on behaving as-is.
export const uploadTechnicalStats = async (rows) => {
  const res = await fetch(`${BASE_URL}/api/technical-stats/upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rows),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.error || `API error: ${res.status}`);
  return body;
};
export const getTechnicalStats    = ()          => api('/api/technical-stats');
export const deleteTechnicalStats = (matchId)   => api(`/api/technical-stats/${matchId}`, 'DELETE');

// ── Technical stats (KNVB CSV import, 2026-08-17) ────────────────────────────────
// Second source alongside SciSports above — separate endpoint/function (not a format param
// on the existing one) since the two CSVs have entirely different column sets and the backend
// importer is a separate function (ingestKnvbCsv) reading them directly, same reasoning as the
// SciSports upload bypassing api() for its own specific error message.
export const uploadTechnicalStatsKnvb = async (rows) => {
  const res = await fetch(`${BASE_URL}/api/technical-stats/upload-knvb`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rows),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.error || `API error: ${res.status}`);
  return body;
};

// ── CMJ (ForceDecks CSV import) ──────────────────────────────────────────────────
// Writes into the same test_cmj table/columns the manual Physical Test Input CMJ form uses —
// see server.production.js's /api/cmj/upload for the upsert.
export const uploadCmjTests = (rows) => api('/api/cmj/upload', 'POST', rows);

// ── Drills ─────────────────────────────────────────────────────────────────────
export const saveDrillBatch  = (drills)   => api('/api/drills/batch', 'POST', drills);
export const getDrills       = (sessionId)=> api(`/api/drills?session_id=${sessionId}`);
export const getMatchDrills       = ()                           => api('/api/drills/match');
export const getPlayerMatchDrills = (playerName, sessionType) => {
  const q = new URLSearchParams({ player_name: playerName, ...(sessionType ? { session_type: sessionType } : {}) });
  return api(`/api/drills/player?${q}`);
};
export const getPlayerMatchSessions = (playerName) => {
  const q = new URLSearchParams({ player_name: playerName });
  return api(`/api/gps/player-matches?${q}`);
};

// ── Comparison Builder ────────────────────────────────────────────────────────
export const getMatchAvailableDrills = matchId => api(`/api/matches/${matchId}/drills`);
export const getMatchDrillMetrics = (matchId, { scope, playerId, metrics }) => {
  const q = new URLSearchParams({ scope, metrics: metrics.join(',') });
  if (scope === 'player' && playerId) q.set('player_id', playerId);
  return api(`/api/matches/${matchId}/drill-metrics?${q}`);
};
export const getComparisonPresets    = ()     => api('/api/comparison-presets');
export const createComparisonPreset  = data   => api('/api/comparison-presets', 'POST', data);
export const deleteComparisonPreset  = id     => api(`/api/comparison-presets/${id}`, 'DELETE');

// ── Load Scenario Templates ────────────────────────────────────────────────────
export const getLoadScenarios    = ()         => api('/api/load-scenarios');
export const createLoadScenario  = (data)     => api('/api/load-scenarios', 'POST', data);
export const updateLoadScenario  = (id, data) => api(`/api/load-scenarios/${id}`, 'PUT', data);
export const deleteLoadScenario  = (id)       => api(`/api/load-scenarios/${id}`, 'DELETE');

export const syncStaging = () => api('/api/admin/sync-staging', 'POST');

// ── GPS & Load Week Summary (staging only) ──────────────────────────────────────
export const getGpsWeekSummaryScenarios = () => api('/api/gps/week-summary/scenarios');
export const getGpsWeekSummary = (scenarioId) => api(`/api/gps/week-summary${scenarioId ? `?scenarioId=${encodeURIComponent(scenarioId)}` : ''}`);

// ── Player name aliases ──────────────────────────────────────────────────────
export const getPlayerAliases    = ()     => api('/api/player-aliases');
export const createPlayerAlias   = (data) => api('/api/player-aliases', 'POST', data);
export const deletePlayerAlias   = (id)   => api(`/api/player-aliases/${id}`, 'DELETE');

// ── Team Overview ──────────────────────────────────────────────────────────────
export const getTeamOverview = () => api('/api/team-overview');
export const getNextMatch    = () => api('/api/next-match');
export const updateNextMatch = (data) => api('/api/next-match', 'PUT', data);

// ── Availability ──────────────────────────────────────────────────────────────
export const getAvailability = (dateFrom, dateTo, mode) =>
  api(`/api/availability?date_from=${dateFrom}&date_to=${dateTo}&mode=${mode || 'total'}`);
export const getAvailabilityOverrides   = (playerId) => api(`/api/availability-overrides${playerId ? `?player_id=${playerId}` : ''}`);
export const createAvailabilityOverride = (data)     => api('/api/availability-overrides', 'POST', data);
export const deleteAvailabilityOverride = (id)       => api(`/api/availability-overrides/${id}`, 'DELETE');
export const getLegacyStatusCodes       = ()         => api('/api/legacy-status-codes');

// ── Push notifications ────────────────────────────────────────────────────────
export const getVapidPublicKey       = ()              => api('/api/push/vapid-public-key');
export const sendRPEReminder         = ()              => api('/api/push/rpe-reminder', 'POST');
export const sendWellnessReminder    = ()              => api('/api/push/wellness-reminder', 'POST');
export const getWellnessReminderSettings    = ()     => api('/api/wellness-reminder-settings');
export const updateWellnessReminderSettings = (data) => api('/api/wellness-reminder-settings', 'PUT', data);
export const subscribeToPush         = (data)           => api('/api/push/subscribe', 'POST', data);
export const unsubscribeFromPush     = (endpoint)        => api('/api/push/unsubscribe', 'DELETE', { endpoint });
export const getNotificationPreferences    = (userId)    => api(`/api/notification-preferences/${userId}`);
export const updateNotificationPreferences = (userId, data) => api(`/api/notification-preferences/${userId}`, 'PUT', data);

// ── Medical options ────────────────────────────────────────────────────────────
export const getMedicalOptions  = (category) => api(`/api/medical-options${category ? `?category=${category}` : ''}`);
export const createMedicalOption = (data)    => api('/api/medical-options', 'POST', data);
export const updateMedicalOption = (id, data) => api(`/api/medical-options/${id}`, 'PUT', data);

// ── Injuries ──────────────────────────────────────────────────────────────────
export const getInjuries       = (playerId)   => api(`/api/injuries${playerId ? `?player_id=${playerId}` : ''}`);
export const createInjury      = (data)        => api('/api/injuries', 'POST', data);
export const updateInjuryStage = (id, data)    => api(`/api/injuries/${id}/stage`, 'PUT', data);
export const updateInjury      = (id, data)    => api(`/api/injuries/${id}`, 'PUT', data);
export const deleteInjury      = (id)          => api(`/api/injuries/${id}`, 'DELETE');
export const getInjuryHistory  = (id)          => api(`/api/injuries/${id}/history`);
export const addInjuryNote     = (id, data)    => api(`/api/injuries/${id}/notes`, 'POST', data);
export const getInjuryNotes    = (id)          => api(`/api/injuries/${id}/notes`);
export const updateInjuryNote  = (id, data)    => api(`/api/injury-notes/${id}`, 'PUT', data);
export const deleteInjuryNote  = (id)          => api(`/api/injury-notes/${id}`, 'DELETE');

// ── Illnesses ─────────────────────────────────────────────────────────────────
export const getIllnesses      = (playerId)   => api(`/api/illnesses${playerId ? `?player_id=${playerId}` : ''}`);
export const createIllness     = (data)        => api('/api/illnesses', 'POST', data);
export const updateIllness     = (id, data)    => api(`/api/illnesses/${id}`, 'PUT', data);
export const addIllnessNote    = (id, data)    => api(`/api/illnesses/${id}/notes`, 'POST', data);
export const getIllnessNotes   = (id)          => api(`/api/illnesses/${id}/notes`);
export const updateIllnessNote = (id, data)    => api(`/api/illness-notes/${id}`, 'PUT', data);
export const deleteIllnessNote = (id)          => api(`/api/illness-notes/${id}`, 'DELETE');

// ── Appointments ──────────────────────────────────────────────────────────────
export const getAppointments   = (playerId)   => api(`/api/appointments${playerId ? `?player_id=${playerId}` : ''}`);
export const createAppointment = (data)        => api('/api/appointments', 'POST', data);
export const updateAppointment = (id, data)    => api(`/api/appointments/${id}`, 'PUT', data);

// ── Physical testing ──────────────────────────────────────────────────────────
export const getTests    = (type, playerId) => api(`/api/tests/${type}${playerId ? `?player_id=${playerId}` : ''}`);
export const createTest  = (type, data)     => api(`/api/tests/${type}`, 'POST', data);
export const getISRTPercentiles = () => api('/api/tests/isrt-percentiles');
export const getSprintPercentiles = () => api('/api/tests/sprint-percentiles');
export const getSquadProfiling = () => api('/api/tests/squad-profiling');
export const deleteTestSession = (playerId, testDate) => api(`/api/tests/session?player_id=${playerId}&test_date=${testDate}`, 'DELETE');
export const deleteTest        = (type, playerId, testDate) => api(`/api/tests/${type}?player_id=${playerId}&test_date=${testDate}`, 'DELETE');
export const syncPassingDrillTests          = () => api('/api/tests/passing-drill/sync', 'POST');
export const syncIsrtMaxHR                  = () => api('/api/tests/isrt/sync-hr', 'POST');
export const getPassingDrillCalibration     = () => api('/api/passing-drill-calibration');
export const updatePassingDrillCalibration  = (data) => api('/api/passing-drill-calibration', 'PUT', data);
export const getTestInputAccess     = () => api('/api/test-input-access');
export const grantTestInputAccess   = (data) => api('/api/test-input-access', 'POST', data);
export const revokeTestInputAccess  = (accountId) => api(`/api/test-input-access/${accountId}`, 'DELETE');

export const getTestNotes = (playerId) => api(`/api/test-notes${playerId ? `?player_id=${playerId}` : ''}`);
export const saveTestNote = (data)     => api('/api/test-notes', 'POST', data);

// ── Medical Log ───────────────────────────────────────────────────────────────
export const getMedicalLog    = (params = {}) => {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v)));
  return api(`/api/medical-log${q.toString() ? `?${q}` : ''}`);
};
export const createMedicalLog = (data) => api('/api/medical-log', 'POST', data);
export const deleteMedicalLog = (id)   => api(`/api/medical-log/${id}`, 'DELETE');

// ── Forms ─────────────────────────────────────────────────────────────────────
export const getForms         = ()             => api('/api/forms');
export const createForm       = (data)         => api('/api/forms', 'POST', data);
export const updateForm       = (id, data)     => api(`/api/forms/${id}`, 'PUT', data);
export const deleteForm       = (id)           => api(`/api/forms/${id}`, 'DELETE');
export const sendForm         = (id, data)     => api(`/api/forms/${id}/send`, 'POST', data);
export const closeForm        = (id)           => api(`/api/forms/${id}/close`, 'POST');
export const getFormResponses = (id)           => api(`/api/forms/${id}/responses`);
export const getPendingForms  = (playerId)     => api(`/api/players/me/forms/pending?player_id=${playerId}`);
export const getFormToFill    = (id, playerId) => api(`/api/forms/${id}/fill?player_id=${playerId}`);
export const submitFormResponse = (id, data)   => api(`/api/forms/${id}/respond`, 'POST', data);

// ── Week schedule ─────────────────────────────────────────────────────────────
export const getScheduleWeeks     = ()               => api('/api/schedule/weeks');
export const getScheduleWeek      = (weekStartDate)  => api(`/api/schedule/weeks/${weekStartDate}`);
export const getScheduleDay       = (date)           => api(`/api/schedule/day/${date}`);
export const createScheduleWeek   = (data)           => api('/api/schedule/weeks', 'POST', data);
export const patchScheduleDay     = (dayId, data)    => api(`/api/schedule/days/${dayId}`, 'PATCH', data);
export const finalizeScheduleWeek = (id)             => api(`/api/schedule/weeks/${id}/finalize`, 'POST');
export const resyncScheduleWeek   = (id)             => api(`/api/schedule/weeks/${id}/resync`, 'POST');
export const duplicateScheduleWeek = (id, newWeekStartDate) =>
  api(`/api/schedule/weeks/${id}/duplicate`, 'POST', { new_week_start_date: newWeekStartDate });

// ── Period League (ssg_*) ────────────────────────────────────────────────────
// No auth middleware anywhere in this app (see server.staging.js's Forms section comment) —
// requesterRole is trusted straight from the caller, same convention as Week Schedule's
// PATCH /api/schedule/days/:dayId. Query string for GETs and for the points array PUT (its body
// IS the array, so requesterRole can't live inside it); body field everywhere else.
export const getSsgPeriods   = (season) => api(`/api/ssg/periods${season ? `?season=${encodeURIComponent(season)}` : ''}`);
export const createSsgPeriod = (data, requesterRole) => api('/api/ssg/periods', 'POST', { ...data, requesterRole });
export const updateSsgPeriod = (id, data, requesterRole) => api(`/api/ssg/periods/${id}`, 'PUT', { ...data, requesterRole });
export const closeSsgPeriod  = (id, requesterRole, force) =>
  api(`/api/ssg/periods/${id}/close?requesterRole=${requesterRole}${force ? '&force=true' : ''}`, 'POST');

export const getSsgSessions = (params = {}) => {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')));
  return api(`/api/ssg/sessions${q.toString() ? `?${q}` : ''}`);
};
export const getSsgSession    = (id)   => api(`/api/ssg/sessions/${id}`);
export const createSsgSession = (data, requesterRole) => api('/api/ssg/sessions', 'POST', { ...data, requesterRole });
export const updateSsgSession = (id, data, requesterRole) => api(`/api/ssg/sessions/${id}`, 'PUT', { ...data, requesterRole });
export const deleteSsgSession = (id, requesterRole) => api(`/api/ssg/sessions/${id}?requesterRole=${requesterRole}`, 'DELETE');
export const getSsgSessionPoints = (id) => api(`/api/ssg/sessions/${id}/points`);
export const putSsgSessionPoints = (id, rows, requesterRole) =>
  api(`/api/ssg/sessions/${id}/points?requesterRole=${requesterRole}`, 'PUT', rows);
export const publishSsgSession = (id, requesterRole) => api(`/api/ssg/sessions/${id}/publish?requesterRole=${requesterRole}`, 'POST');

export const getSsgLeaderboard = (periodId, scope = 'period') => api(`/api/ssg/leaderboard?period_id=${periodId}&scope=${scope}`);
export const getSsgProgression = (periodId) => api(`/api/ssg/leaderboard/progression?period_id=${periodId}`);
export const getSsgPlayerSummary = (playerId, periodId) => api(`/api/ssg/player/${playerId}/summary?period_id=${periodId}`);
export const getSsgHeadToHead = (a, b, periodId) => api(`/api/ssg/head-to-head?a=${a}&b=${b}${periodId ? `&period_id=${periodId}` : ''}`);
export const getSsgBadges     = (periodId) => api(`/api/ssg/badges?period_id=${periodId}`);
export const getSsgHallOfFame = (season) => api(`/api/ssg/hall-of-fame${season ? `?season=${encodeURIComponent(season)}` : ''}`);

// Dressing-room TV — /api/ssg/tv is token-only (no requesterRole, no session), the kiosk route
// never logs in. Token admin (list/create/revoke) is the usual requesterRole-gated pattern.
export const getSsgTv = (token) => api(`/api/ssg/tv?token=${encodeURIComponent(token)}`);
export const getSsgTvTokens   = (requesterRole) => api(`/api/ssg/tv-tokens?requesterRole=${requesterRole}`);
export const createSsgTvToken = (data, requesterRole) => api('/api/ssg/tv-tokens', 'POST', { ...data, requesterRole });
export const updateSsgTvToken = (token, data, requesterRole) => api(`/api/ssg/tv-tokens/${encodeURIComponent(token)}`, 'PUT', { ...data, requesterRole });
